import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"

import {
  getOrgRevenue,
  getOrgMemberCount,
  getOrgRevenueTrend,
  getOrgMemberTrend,
  type OrgRevenueResponse,
  type OrgMemberCountResponse,
  type OrgRevenueTrendResponse,
  type OrgMemberTrendResponse,
} from "@/libs/orgAnalyticsApi"

/**
 * Org-analytics query hooks. All four are disabled until an org id is known and
 * carry no business logic — they only fetch the backend-computed rollups. The
 * date windows are derived here (30-day summary, 6-bucket / ~6-month trend) and
 * memoized so they don't churn the query keys on every render.
 */

const TREND_BUCKETS = 6
const DAY_MS = 24 * 60 * 60 * 1000

export function useSummaryRange() {
  return useMemo(() => {
    const now = new Date()
    const start = new Date(now.getTime() - 30 * DAY_MS)
    return { start: start.toISOString(), end: now.toISOString() }
  }, [])
}

export function useTrendRange() {
  return useMemo(() => {
    const now = new Date()
    const start = new Date(now.getTime() - 180 * DAY_MS)
    return { start: start.toISOString(), end: now.toISOString() }
  }, [])
}

export function useOrgRevenue(orgId: string | undefined, start: string, end: string) {
  return useQuery<OrgRevenueResponse>({
    queryKey: ["org-revenue", orgId, start, end],
    queryFn: () => getOrgRevenue(orgId as string, start, end),
    enabled: !!orgId,
  })
}

export function useOrgMemberCount(orgId: string | undefined) {
  return useQuery<OrgMemberCountResponse>({
    queryKey: ["org-member-count", orgId],
    queryFn: () => getOrgMemberCount(orgId as string),
    enabled: !!orgId,
  })
}

export function useOrgRevenueTrend(orgId: string | undefined, start: string, end: string) {
  return useQuery<OrgRevenueTrendResponse>({
    queryKey: ["org-revenue-trend", orgId, start, end, TREND_BUCKETS],
    queryFn: () => getOrgRevenueTrend(orgId as string, start, end, TREND_BUCKETS),
    enabled: !!orgId,
  })
}

export function useOrgMemberTrend(orgId: string | undefined, start: string, end: string) {
  return useQuery<OrgMemberTrendResponse>({
    queryKey: ["org-member-trend", orgId, start, end, TREND_BUCKETS],
    queryFn: () => getOrgMemberTrend(orgId as string, start, end, TREND_BUCKETS),
    enabled: !!orgId,
  })
}
